
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import WorkplaceTimer from '@/modules/TMSModule/components/WorkplaceTimer/index.vue'

// @module components
import TripsDetailedTMSRow from '@/modules/TMSModule/components/TripsDetailedTMSRow/index.vue'
import TripsDetailedBidDetailsRow from '@/modules/TMSModule/components/TripsDetailedBidDetailsRow/index.vue'
import TripsDetailedDelayInfoRow from '@/modules/TMSModule/components/TripsDetailedDelayInfoRow/index.vue'

// @types
import { DelayInfo, TripComment, TripDetailedTMS, RescheduleTtnPayload } from '@/new-services/types/tms'
import { UserT } from '@/core/types/users.types'

// @services
import tmsAPI from '@/new-services/tms.service'
import { users } from '@/core/realisations/users.realisation'
import { rpp } from '@/core/realisations/rpp.realisation'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf, integer } from '@vuelidate/validators'
import useAccount from '@/common/composable/useAccount'
import { useFile } from '@/common/composable/useFile'

import { TRIPS_STATUSES } from '@/core/constants/tms.constants'

import { formatDateWithTimeFromBackend, formatDateFromBackend, formatDateTime } from '@/common/helpers/formatDate'

const TABLE_COLUMNS = [
    {
        key: 'index',
        name: '№',
    },
    {
        key: 'number',
        name: 'Номер ТТН',
    },
    {
        key: 'direction',
        name: 'Направление',
    },
    {
        key: 'totalWeight',
        name: 'Вес',
    },
    {
        key: 'createdAt',
        name: 'Дата создания',
    },
    {
        key: 'expectedDate',
        name: 'Плановая дата прибытия',
    },
    {
        key: 'expectedDateWithSla',
        name: 'Расчёт по SLA'
    },
    {
        key: 'arrivalDate',
        name: 'Дата прибытия (факт)',
    },
    {
        key: 'status',
        name: 'Статус',
    },
    {
        key: 'actions',
        name: ''
    }
]

const BID_DETAILS_COLUMNS = [
    {
        key: 'name',
        name: 'Имя',
    },
    {
        key: 'role',
        name: 'Должность',
    },
    {
        key: 'costOfTransportation',
        name: 'Запрашиваемая стоимость',
    },
    {
        key: 'status',
        name: 'Ответ',
    },
    {
        key: 'comment',
        name: 'Комментарий',
    },
    {
        key: '',
        name: 'Этап',
    },
]

const DELAY_INFO_COLUMNS = [
    {
        key: 'name',
        name: 'Ответственный логист',
    },
    {
        key: 'state',
        name: 'Этап',
    },
    {
        key: 'expectedDate',
        name: 'План. дата',
    },
    {
        key: 'date',
        name: 'Факт. дата',
    },
    {
        key: 'slaComment',
        name: 'SLA',
    },
]

const DELAY_INFO_COMMENTS_COLUMNS = [
    {
        key: 'name',
        name: 'Ответственный логист',
    },
    {
        key: 'state',
        name: 'Этап',
    },
    {
        key: 'expectedDate',
        name: 'План. дата',
    },
    {
        key: 'date',
        name: 'Факт. дата',
    },
    {
        key: 'slaComment',
        name: 'SLA',
    },
    {
        key: 'comment',
        name: 'Комментарий',
    },
]

const CLOSE_DETAILS_COLUMNS = [
    {
        key: 'initiator',
        name: 'Инициатор',
    },
    {
        key: 'commentInfo',
        name: 'Комментарий',
    },
    {
        key: 'plannedDate',
        name: 'План. дата',
    },
    {
        key: 'completedDate',
        name: 'Закрытие дата',
    },
    {
        key: 'rpp',
        name: 'РПП',
    },
]

export default defineComponent({
    components: {
        PageTitle,
        Loader,
        NewButton,
        NewTable,
        NewModal,
        NewSelect,
        TheTextarea,
        Cheap,
        NewInput,
        TripsDetailedBidDetailsRow,
        TripsDetailedDelayInfoRow,
        'trips-detailed-tms-row': TripsDetailedTMSRow,
        WorkplaceTimer,
    },
    setup() {
        const notifications = useNotifications()
        const route = useRoute()
        const tripId = Number(route.params.id as string)
        const { havePermissions } = useAccount()
        const { saveFile } = useFile()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_trip_print_loading: false,
            //
            trip: null as TripDetailedTMS | null,
            //
            is_comments: false,
            is_comments_loading: false,
            stateId: 1,
            commentIdInbound: null as number | null,
            commentIdOutbound: null as number | null,
            delayItem: null as DelayInfo | null,
            closedRows: [] as {
                initiator: string
                commentInfo: string
                plannedDate: string
                completedDate: string
                rpp: { id: number; pbsNumber: string } | null
            }[],
        })

        const update_popup = reactive({
            is_opened: false,
            is_loading: false,
            ttnId: null as number | null,
            expectedDate: null as string | null,
            newExpectedDate: null as string | null,
            commentId: null as number | null,
            commentInfo: null as string | null,
        })

        const update_popup_form$ = useVuelidate(
            {
                newExpectedDate: { required },
                commentId: { required }
            },
            update_popup
        )

        const state_cancel = reactive({
            is_open: false,
            is_loading: false,
            //
            commentId: null as number | null,
            commentInfo: null as string | null,
        })

        const state_cost = reactive({
            is_open: false,
            is_loading: false,
            cost: null as number | null,
        })

        const comments_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as TripComment[],
        })

        const delay_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as DelayInfo[],
        })

        const done_state = reactive({
            is_loading: false,
            is_opened: false,
            comment: null as string | null,
            initiatorId: null as number | null,
            closingDate: null as string | null,
        })

        const done_state_validation$ = useVuelidate(
            {
                comment: { required },
                initiatorId: { required },
                closingDate: { required },
            },
            done_state
        )

        const isComment = computed(() => state_cancel.commentId === 17)

        const CancelTripValidation = {
            commentId: { required },
            commentInfo: { required: requiredIf(() => isComment.value) },
        }

        const EditCostValidation = {
            cost: { required, integer },
        }

        const state_form$ = useVuelidate(CancelTripValidation, state_cancel)
        const cost_form$ = useVuelidate(EditCostValidation, state_cost)

        //

        const onStartUpdateTtn = (payload: { ttnId: number; expectedDate: string | null }) => {
            update_popup.ttnId = payload.ttnId
            update_popup.expectedDate = payload.expectedDate
            update_popup.is_opened = true
        }

        const onCancelUpdateTtn = () => {
            update_popup.is_opened = false
            update_popup.ttnId = null
            update_popup.expectedDate = null
            update_popup.newExpectedDate = null
            update_popup.commentId = null
            update_popup.commentInfo = null

            update_popup_form$.value.$reset()
        }

        const onSubmitUpdateTtn = async () => {
            update_popup_form$.value.$touch()
            if (update_popup_form$.value.$invalid) return

            try {
                update_popup.is_loading = true

                const dateParts = (update_popup.newExpectedDate as string).split('T')
                const newExpectedDate = `${dateParts[0]} ${dateParts[1]}:00`

                const payload: RescheduleTtnPayload = {
                    ttnId: update_popup.ttnId as number,
                    expectedDate: update_popup.expectedDate,
                    newExpectedDate,
                    commentId: update_popup.commentId as number,
                    commentInfo: update_popup.commentInfo
                }

                await tmsAPI.rescheduleTtn(tripId, payload)

                notifications.notify({ type: 'success', title: 'ТТН сохранена' })
                onCancelUpdateTtn()

                loadTrip()
            } catch (error) {
                notifications.notifyError({ error, title: 'При редактировании ТТН произошла ошибка' })
            } finally {
                update_popup.is_loading = false
            }
        }

        const loadTrip = async () => {
            state.closedRows = []

            try {
                state.is_loading = true

                const response = await tmsAPI.getTrip(tripId)
                state.trip = response.data.data

                if (state.trip && state.trip.isEarlyCompleted && state.trip.earlyCompletedDetails) {
                    const rppResponse = await rpp.getLtlRppList({
                        page: 1,
                        limit: 9999,
                        trip_number: state.trip.tripNumber,
                    })
                    const rppItem = rppResponse.data[0]
                    const plannedDate = state.trip.dates.expectedDate || '-'

                    state.closedRows.push({
                        initiator: state.trip.earlyCompletedDetails.initiator,
                        commentInfo: state.trip.earlyCompletedDetails.commentInfo,
                        completedDate: state.trip.earlyCompletedDetails.completedDate,
                        plannedDate,
                        rpp: Boolean(rppItem)
                            ? { id: rppItem.id, pbsNumber: rppItem.pbsNumber }
                            : null,
                    })
                }

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении детальной информации произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadComments = async () => {
            try {
                comments_state.is_loading = true

                const response = await tmsAPI.getTripComments()

                comments_state.list = response.data.data
                comments_state.is_failed = false
            } catch (error) {
                console.error(error)
                comments_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка комментариев произошла ошибка',
                })
            } finally {
                comments_state.is_loading = false
            }
        }

        const loadGeneralComments = async () => {
            try {
                data_state.general_comments.is_loading = true

                const response = await tmsAPI.getTripComments()

                data_state.general_comments.list = response.data.data
                data_state.general_comments.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.general_comments.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка комментариев произошла ошибка',
                })
            } finally {
                data_state.general_comments.is_loading = false
            }
        }

        const data_state = reactive({
            general_comments: {
                is_loading: false,
                is_failed: false,
                list: [] as TripComment[],
            },
            comments: {
                is_loading: false,
                is_failed: false,
                list: [] as TripComment[],
            },
            initiators: {
                is_loading: false,
                is_failed: false,
                list: [] as UserT[],
            },
        })

        const loadCommentsByStateId = async () => {
            if (!isCommentInbound.value && !isCommentOutbound.value) return

            try {
                data_state.comments.is_loading = true

                const response = await tmsAPI.getTripComments(state.stateId)
                data_state.comments.list = response.data.data

                data_state.comments.is_failed = false
            } catch (error) {
                data_state.comments.is_failed = true
                console.error(error)
            } finally {
                data_state.comments.is_loading = false
            }
        }

        const loadDelayInfo = async () => {
            try {
                delay_state.is_loading = true

                const response = await tmsAPI.getDelayInfo(tripId)

                delay_state.list = response.data.data
                delay_state.is_failed = false
            } catch (error) {
                console.error(error)
                delay_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка произошла ошибка',
                })
            } finally {
                delay_state.is_loading = false
            }
        }

        const toggleCancel = () => {
            state_cancel.is_open = !state_cancel.is_open

            state_cancel.commentId = null
            state_cancel.commentInfo = null
        }

        const cancelTrip = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state_cancel.is_loading = true

                const payload = {
                    commentId: state_cancel.commentId as number,
                    commentInfo: state_cancel.commentInfo,
                }

                await tmsAPI.cancelTrip(tripId, payload)

                toggleCancel()
                loadTrip()
                notifications.notify({ title: 'Рейс успешно отменен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При отмене произошла ошибка',
                })
            } finally {
                state_cancel.is_loading = false
            }
        }

        //

        const pageTitle = computed(() => {
            if (state.trip) {
                const { tripNumber, fromCity, toCity, dates } = state.trip
                return `Рейс №${tripNumber} ${fromCity.name} - ${toCity.name} от ${formatDateTime(dates.createdDate)}`
            }

            return 'Рейс №... ... от ...'
        })

        const isCancel = computed(() => {
            return (
                havePermissions(['tms.trip-set-cancel']) &&
                state.trip &&
                state.trip.status &&
                state.trip.status.id === 4
            )
        })

        const getCheapClass = computed(() => {
            let className = 'ml-3 small '

            if (state.trip && state.trip.status) {
                className += TRIPS_STATUSES[state.trip.status.id - 1]
            }

            return className
        })

        const toggleCost = () => {
            state_cost.is_open = !state_cost.is_open
        }

        const editCost = async () => {
            cost_form$.value.$touch()
            if (cost_form$.value.$invalid || !state.trip) return

            try {
                state_cost.is_loading = true

                await tmsAPI.editCost(state.trip.id, state_cost.cost as number)

                toggleCost()
                loadTrip()
                notifications.notify({
                    title: 'Себестоимость успешно отредактирована',
                    type: 'success',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании произошла ошибка',
                })
            } finally {
                state_cost.is_loading = false
            }
        }

        const getDelayRows = computed(() => {
            return delay_state.list.filter((item) => item.isDelayed && !item.comment)
        })

        const toggleComments = () => {
            const delayItem = getDelayRows.value.find((item) => !item.comment)

            if (delayItem) {
                state.delayItem = { ...delayItem }
                state.stateId = delayItem.stateKey === 'outbound' ? 1 : 2
                state.is_comments = !state.is_comments
            } else return

            loadCommentsByStateId()
        }

        const isCommentInbound = computed(() => {
            return state.delayItem && state.delayItem.stateKey === 'inbound'
        })
        const isCommentOutbound = computed(() => {
            return state.delayItem && state.delayItem.stateKey === 'outbound'
        })

        const getCommentsTitle = computed(() => {
            return 'Укажите причину опоздания рейса'
        })
        const isComments = computed(
            () => state.is_comments && (isCommentInbound.value || isCommentOutbound.value)
        )

        const sendComment = async () => {
            if ((!state.commentIdInbound && !state.commentIdOutbound) || !state.trip) return

            try {
                state.is_comments_loading = true
                if (isCommentInbound.value && state.commentIdInbound) {
                    await tmsAPI.sendComment(state.trip.id, state.commentIdInbound, state.stateId)
                }
                if (isCommentOutbound.value && state.commentIdOutbound) {
                    await tmsAPI.sendComment(state.trip.id, state.commentIdOutbound, state.stateId)
                }
                notifications.notify({ title: 'Изменения успешно сохранены', type: 'success' })
                toggleComments()
                loadTrip()
                loadDelayInfo()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При отправке произошла ошибка',
                })
            } finally {
                state.is_comments_loading = false
            }
        }

        const loadInitiators = async () => {
            try {
                data_state.initiators.is_loading = true

                const response = await users.getLogists(1, undefined, 0, undefined, 9999)
                data_state.initiators.list = response.data

                data_state.initiators.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.initiators.is_failed = true
            } finally {
                data_state.initiators.is_loading = false
            }
        }

        //

        onBeforeMount(() => {
            loadComments()
            loadGeneralComments()
            loadTrip()
            loadDelayInfo()
            loadInitiators()
        })

        //

        const onDone = () => {
            const now = new Date(Date.now())
            done_state.closingDate = now.toISOString().split('T')[0]
            done_state.is_opened = true
        }

        const onDoneClose = () => {
            done_state.is_opened = false
            done_state.comment = null
            done_state.initiatorId = null
            done_state.closingDate = null
        }

        const getNowHours = () => {
            const date = new Date(Date.now())

            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            const seconds = String(date.getSeconds()).padStart(2, '0')

            return `${hours}:${minutes}:${seconds}`
        }

        const onDoneSubmit = async () => {
            done_state_validation$.value.$touch()
            if (done_state_validation$.value.$invalid) return

            try {
                done_state.is_loading = true

                await tmsAPI.completeTrip(tripId, {
                    initiatorId: done_state.initiatorId as number,
                    commentInfo: done_state.comment as string,
                    completeDate: `${done_state.closingDate} ${getNowHours()}`,
                })

                notifications.notify({ type: 'success', title: 'Рейс завершен' })
                onDoneClose()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При завершении рейса произошла ошибка' })
            } finally {
                done_state.is_loading = false
            }
        }

        const printTrip = async () => {
            if (state.is_trip_print_loading || !state.trip) return

            try {
                state.is_trip_print_loading = true

                const response = await tmsAPI.printTrip(state.trip.id)
                saveFile(response.data, `Рейс (${state.trip.tripNumber}).xlsx`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Произошла ошибка при скачивании печати рейса`,
                })
            } finally {
                state.is_trip_print_loading = false
            }
        }

        const isComplete = computed(() => state.trip?.status?.id === 2)

        const getTodayDate = computed(() => {
            const today = new Date()
            const dd = String(today.getDate()).padStart(2, '0')
            const mm = String(today.getMonth() + 1).padStart(2, '0')
            const yyyy = today.getFullYear()

            return `${dd}-${mm}-${yyyy}`
        })

        return {
            havePermissions,
            state,
            update_popup,
            update_popup_form$,
            state_cancel,
            state_cost,
            delay_state,
            data_state,
            //
            done_state,
            done_state_validation$,
            onDone,
            onDoneClose,
            onDoneSubmit,
            //
            comments_state,
            state_form$,
            cost_form$,
            //
            loadTrip,
            toggleCancel,
            cancelTrip,
            //
            isCancel,
            pageTitle,
            tripId,
            isComment,
            getCheapClass,
            toggleCost,
            editCost,
            getDelayRows,
            toggleComments,
            getCommentsTitle,
            isComments,
            sendComment,
            isCommentInbound,
            isCommentOutbound,
            loadInitiators,
            printTrip,
            isComplete,
            loadComments,
            getTodayDate,
            //
            TABLE_COLUMNS,
            DELAY_INFO_COLUMNS,
            DELAY_INFO_COMMENTS_COLUMNS,
            BID_DETAILS_COLUMNS,
            CLOSE_DETAILS_COLUMNS,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
            formatDateTime,
            //
            loadGeneralComments,
            onStartUpdateTtn,
            onCancelUpdateTtn,
            onSubmitUpdateTtn
        }
    },
})
